import { useState, useEffect } from 'react';
import axiosInstance from '@/utils/axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { TestTube, Baby, FlaskConical, Building2 } from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import DateInput from './DateInput';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF6B6B', '#4ECDC4', '#45B7D1'];

const CATEGORY_COLORS = {
  publico_geral: '#0088FE',
  gestante: '#FF69B4',
  complementares: '#8B5CF6'
};

const CATEGORY_LABELS = {
  publico_geral: 'Público Geral',
  gestante: 'Gestante',
  complementares: 'Complementares'
};

export default function ExamStatistics({ healthUnits = [], simplified = false }) {
  const [stats, setStats] = useState(null);
  const [categoryStats, setCategoryStats] = useState(null);
  const [unitStats, setUnitStats] = useState(null);
  const [complementaresStats, setComplementaresStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedUnit, setSelectedUnit] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedExam, setSelectedExam] = useState('');

  const fetchStats = async () => {
    try {
      setLoading(true);
      
      // Build query params
      const params = {};
      if (selectedUnit && selectedUnit !== 'all') params.unit_id = selectedUnit;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      // Fetch main statistics
      const [mainStats, catStats, unitRes, compStats] = await Promise.all([
        axiosInstance.get('/exams/statistics', { params }),
        axiosInstance.get('/exams/statistics/publico-vs-gestante', { params }),
        axiosInstance.get('/exams/statistics/by-unit', { params: { ...params, exam_name: selectedExam || undefined } }),
        axiosInstance.get('/exams/statistics/complementares', { params })
      ]);

      setStats(mainStats.data);
      setCategoryStats(catStats.data);
      setUnitStats(unitRes.data.stats_by_unit);
      setComplementaresStats(compStats.data);
    } catch (error) {
      console.error('Erro ao buscar estatísticas de exames:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [selectedUnit, startDate, endDate, selectedExam]);

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Prepare data for charts
  const rankingData = stats.exam_ranking?.slice(0, 15).map(item => ({
    name: item.name.length > 25 ? item.name.substring(0, 25) + '...' : item.name,
    fullName: item.name,
    count: item.count,
    category: item.category
  })) || [];

  const categoryPieData = [
    { name: 'Público Geral', value: categoryStats?.publico_geral || 0, color: CATEGORY_COLORS.publico_geral },
    { name: 'Gestante', value: categoryStats?.gestante || 0, color: CATEGORY_COLORS.gestante },
    { name: 'Complementares', value: categoryStats?.complementares || 0, color: CATEGORY_COLORS.complementares }
  ].filter(item => item.value > 0);

  const unitChartData = unitStats?.slice(0, 10).map(item => ({
    name: item.unit_name.length > 20 ? item.unit_name.substring(0, 20) + '...' : item.unit_name,
    fullName: item.unit_name,
    count: item.count
  })) || [];

  const complementaresData = complementaresStats?.complementares_ranking?.slice(0, 10).map(item => ({
    name: item.name,
    count: item.count
  })) || [];

  // Simplified version for Reports page
  if (simplified) {
    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <TestTube className="w-8 h-8 mx-auto text-primary mb-2" />
                <p className="text-2xl font-bold">{stats.total_exams_requested || 0}</p>
                <p className="text-sm text-muted-foreground">Total de Exames</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <TestTube className="w-8 h-8 mx-auto text-blue-500 mb-2" />
                <p className="text-2xl font-bold">{categoryStats?.publico_geral || 0}</p>
                <p className="text-sm text-muted-foreground">Público Geral</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <Baby className="w-8 h-8 mx-auto text-pink-500 mb-2" />
                <p className="text-2xl font-bold">{categoryStats?.gestante || 0}</p>
                <p className="text-sm text-muted-foreground">Gestante</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <FlaskConical className="w-8 h-8 mx-auto text-purple-500 mb-2" />
                <p className="text-2xl font-bold">{categoryStats?.complementares || 0}</p>
                <p className="text-sm text-muted-foreground">Complementares</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Top Exams Chart */}
        {rankingData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TestTube className="w-5 h-5" />
                Exames Mais Solicitados
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={rankingData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 11 }} />
                    <Tooltip 
                      formatter={(value, name, props) => [value, props.payload.fullName]}
                    />
                    <Bar 
                      dataKey="count" 
                      fill="#0088FE"
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Full version for Admin Dashboard
  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filtros de Análise</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>Unidade de Saúde</Label>
              <Select value={selectedUnit} onValueChange={setSelectedUnit}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Todas as unidades" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as unidades</SelectItem>
                  {healthUnits.map((unit) => (
                    <SelectItem key={unit.id} value={unit.id}>
                      {unit.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Data Inicial</Label>
              <DateInput 
                value={startDate}
                onChange={setStartDate}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Data Final</Label>
              <DateInput 
                value={endDate}
                onChange={setEndDate}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Exame Específico (UBS)</Label>
              <Select value={selectedExam || "all"} onValueChange={(v) => setSelectedExam(v === "all" ? "" : v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Todos os exames" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os exames</SelectItem>
                  {stats.exam_ranking?.slice(0, 20).map((exam) => (
                    <SelectItem key={exam.name} value={exam.name}>
                      {exam.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-6">
            <div className="text-center">
              <TestTube className="w-10 h-10 mx-auto text-blue-600 mb-2" />
              <p className="text-3xl font-bold text-blue-700">{stats.total_exams_requested || 0}</p>
              <p className="text-sm text-blue-600 font-medium">Total de Exames Solicitados</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-6">
            <div className="text-center">
              <TestTube className="w-10 h-10 mx-auto text-green-600 mb-2" />
              <p className="text-3xl font-bold text-green-700">{categoryStats?.publico_geral || 0}</p>
              <p className="text-sm text-green-600 font-medium">Público Geral</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-pink-50 to-pink-100 border-pink-200">
          <CardContent className="pt-6">
            <div className="text-center">
              <Baby className="w-10 h-10 mx-auto text-pink-600 mb-2" />
              <p className="text-3xl font-bold text-pink-700">{categoryStats?.gestante || 0}</p>
              <p className="text-sm text-pink-600 font-medium">Gestante</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="pt-6">
            <div className="text-center">
              <FlaskConical className="w-10 h-10 mx-auto text-purple-600 mb-2" />
              <p className="text-3xl font-bold text-purple-700">{categoryStats?.complementares || 0}</p>
              <p className="text-sm text-purple-600 font-medium">Complementares</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfico 1 - Ranking Geral */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TestTube className="w-5 h-5 text-primary" />
              Ranking Geral de Exames
            </CardTitle>
          </CardHeader>
          <CardContent>
            {rankingData.length > 0 ? (
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={rankingData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={180} tick={{ fontSize: 10 }} />
                    <Tooltip 
                      formatter={(value, name, props) => [value + ' solicitações', props.payload.fullName]}
                    />
                    <Bar 
                      dataKey="count" 
                      fill="#0088FE"
                      radius={[0, 4, 4, 0]}
                    >
                      {rankingData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={CATEGORY_COLORS[entry.category] || COLORS[index % COLORS.length]} 
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[400px] flex items-center justify-center text-muted-foreground">
                Nenhum dado disponível para o período selecionado
              </div>
            )}
          </CardContent>
        </Card>

        {/* Gráfico 4 - Público Geral vs Gestante */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Baby className="w-5 h-5 text-pink-500" />
              Distribuição por Categoria
            </CardTitle>
          </CardHeader>
          <CardContent>
            {categoryPieData.length > 0 ? (
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={categoryPieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={130}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {categoryPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [value + ' exames', 'Quantidade']} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[400px] flex items-center justify-center text-muted-foreground">
                Nenhum dado disponível
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfico 2 - UBS x Exame */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-green-500" />
              Exames por Unidade de Saúde
              {selectedExam && (
                <Badge variant="secondary" className="ml-2">
                  {selectedExam}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {unitChartData.length > 0 ? (
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={unitChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip 
                      formatter={(value, name, props) => [value + ' exames', props.payload.fullName]}
                    />
                    <Bar dataKey="count" fill="#00C49F" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[350px] flex items-center justify-center text-muted-foreground">
                Nenhum dado disponível
              </div>
            )}
          </CardContent>
        </Card>

        {/* Gráfico 3 - Exames Complementares */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-purple-500" />
              Exames Complementares (Não Padrão SUS)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {complementaresData.length > 0 ? (
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={complementaresData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(value) => [value + ' solicitações', 'Quantidade']} />
                    <Bar dataKey="count" fill="#8B5CF6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[350px] flex items-center justify-center text-muted-foreground">
                Nenhum exame complementar solicitado no período
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
